// Radha — the direct-path shell: one app, one login, a tab per module.
//
// This is what "separate modules" means in the product — tabs in one URL, not
// separate deployments. A single Supabase session; the tab bar switches which
// module's panel renders. Adding a module is one line in TABS plus its panel.
//
// The active tab is mirrored to ?tab= so a tab is deep-linkable and survives a
// reload, without pulling in a router.
import React, { useEffect, useState } from "react";

import { signOut, useSession, useMe, RoleContext, canWriteFor } from "../lib/authSupabase";
import { setMoneyContext } from "../lib/money";
import { resolveLocale, t } from "../lib/i18n";
import { LocaleSwitcher } from "../components/LocaleSwitcher";
import LoginCard from "../components/SupabaseLoginCard";
import { CollectionsPanel } from "./CollectionsLive";
import { DisputesPanel } from "./DisputesLive";
import { ContractsPanel } from "./ContractsLive";
import { CrmPanel } from "./CrmLive";
import {
  CashflowPanel, InvoicingPanel,
  ReconciliationPanel, ForecastsPanel, RemindersPanel,
} from "./BatchPanels";
import { PrivacyPanel } from "./PrivacyLive";
import { OverviewPanel } from "./OverviewLive";
import { StatementsPanel } from "./StatementsLive";
import { DunningPanel } from "./DunningLive";
import { CreditNotesPanel } from "./CreditNotesLive";
import { GstPanel } from "./GstLive";
import { DeductionsPanel } from "./DeductionsLive";
import { WithholdingPanel } from "./WithholdingLive";
import { RecurringInvoicesPanel } from "./RecurringInvoicesLive";
import { SettingsPanel } from "./SettingsLive";
import { AutomationPanel } from "./AutomationLive";
import { ThemeToggle } from "../components/ThemeToggle";
import { SkinToggle } from "../components/SkinToggle";
import { CommandPalette, PaletteHint, useCommandPaletteShortcut } from "../components/CommandPalette";
import { SanctuaryParticles, SanctuaryOrbs } from "../components/DevotionalMotifs";
import { CsmLogo } from "../components/CsmLogo";
import { CountrySwitcher, JurisdictionRibbon } from "../components/CountrySwitcher";
import { useViewCountry, useIsOverridden, useReportingCurrency, countryDef } from "../lib/jurisdiction";
import { CurrencySwitcher, useFxRateMap } from "../components/CurrencySwitcher";
import { attachNameLens } from "../lib/companyLens";

const TABS: { id: string; label: string; Panel: React.ComponentType }[] = [
  { id: "overview", label: "Overview", Panel: OverviewPanel },
  { id: "collections", label: "Collections", Panel: CollectionsPanel },
  { id: "disputes", label: "Disputes", Panel: DisputesPanel },
  { id: "contracts", label: "Contracts", Panel: ContractsPanel },
  { id: "invoicing", label: "Invoicing", Panel: InvoicingPanel },
  { id: "recurring", label: "Recurring", Panel: RecurringInvoicesPanel },
  { id: "statements", label: "Statements", Panel: StatementsPanel },
  { id: "credit-notes", label: "Credit Notes", Panel: CreditNotesPanel },
  { id: "gst", label: "GST", Panel: GstPanel },
  { id: "reconciliation", label: "Reconciliation", Panel: ReconciliationPanel },
  { id: "deductions", label: "Deductions", Panel: DeductionsPanel },
  { id: "withholding", label: "Withholding", Panel: WithholdingPanel },
  { id: "cashflow", label: "Cashflow", Panel: CashflowPanel },
  { id: "crm", label: "CRM", Panel: CrmPanel },
  { id: "forecasts", label: "Forecasts", Panel: ForecastsPanel },
  { id: "reminders", label: "Reminders", Panel: RemindersPanel },
  { id: "dunning", label: "Dunning", Panel: DunningPanel },
  { id: "automation", label: "Automation", Panel: AutomationPanel },
  { id: "settings", label: "Settings", Panel: SettingsPanel },
  { id: "privacy", label: "Privacy", Panel: PrivacyPanel },
];

// Sidebar grouping — purely presentational (doesn't affect TAB_ACCESS/visibleTabs);
// a group renders only the tabs the current role can actually see, and disappears
// entirely if none of its tabs are visible.
const TAB_GROUPS: { label: string | null; ids: string[] }[] = [
  { label: null, ids: ["overview"] },
  { label: "group.receivables", ids: ["collections", "disputes", "contracts", "invoicing", "recurring", "statements", "credit-notes", "gst", "reconciliation", "deductions", "withholding"] },
  { label: "group.planning", ids: ["cashflow", "crm", "forecasts"] },
  { label: "group.operations", ids: ["reminders", "dunning", "automation"] },
  { label: "group.admin", ids: ["settings", "privacy"] },
];

// Which tabs each role sees (least privilege). Roles not listed — and the
// broad oversight roles — see everything; auditor sees all but read-only.
const TAB_ACCESS: Record<string, string[]> = {
  collections_head: ["overview", "collections", "disputes", "reminders", "dunning", "reconciliation", "deductions", "cashflow", "statements"],
  ar_manager: ["overview", "collections", "invoicing", "recurring", "statements", "credit-notes", "gst", "reconciliation", "deductions", "withholding", "reminders", "dunning", "automation", "disputes", "settings"],
  sales_rep: ["overview", "crm", "contracts", "forecasts"],
  cs_manager: ["overview", "contracts", "crm", "disputes"],
  legal_head: ["overview", "disputes", "contracts", "privacy"],
};

// "credit-notes" -> "creditNotes", so a tab id maps to its nav.* message key
// without a second lookup table to keep in sync.
const camel = (id: string) => id.replace(/-([a-z])/g, (_, c) => c.toUpperCase());

function visibleTabs(role: string | null | undefined) {
  const allow = role ? TAB_ACCESS[role] : undefined;
  return allow ? TABS.filter((x) => allow.includes(x.id)) : TABS;
}

// The tax tab is the one label that is jurisdiction-specific: "GST" is right in
// India and wrong everywhere else. Everything inside the panel already comes
// from einvoice_regimes (072) — this keeps the nav from contradicting it.
function navKey(id: string, country: string | null | undefined) {
  if (id === "gst" && (country ?? "IN") !== "IN") return "nav.tax";
  return `nav.${camel(id)}`;
}

function initialTab(tabs: typeof TABS): string {
  const q = new URLSearchParams(window.location.search).get("tab");
  return tabs.some((x) => x.id === q) ? (q as string) : tabs[0].id;
}

const ROLE_LABEL: Record<string, string> = {
  cro: "CRO", cfo: "CFO", finance_controller: "Finance Controller",
  collections_head: "Collections Head", ar_manager: "AR Manager",
  cs_manager: "CS Manager", legal_head: "Legal Head", sales_rep: "Sales Rep",
  auditor: "Auditor", admin: "Admin",
};

function Shell() {
  const me = useMe(true);

  // Set the money/date context during THIS render, before any panel renders —
  // React runs a parent's render before its children's, so every fmtMoney()
  // call below already sees the tenant's locale and reporting currency (063).
  // Doing it in an effect instead would let panels paint one frame in the
  // default en-IN/INR, which on a US tenant means a visible ₹ flash.
  // Money follows the lens: a Germany view formats de-DE in EUR. The tenant's
  // OWN base currency stays the unit of the stored book — displayCurrency is a
  // presentation translation applied by lib/money, never a data change.
  // the country lens: jurisdiction-aware labels/panels follow this, the book does not
  const viewCountry = useViewCountry(me?.tenant_country);
  const reporting = useReportingCurrency(me?.tenant_country);
  const { data: fxRates } = useFxRateMap();
  const homeCurrency = me?.tenant_base_currency ?? "INR";
  setMoneyContext({
    locale: reporting.locale || me?.tenant_locale,
    baseCurrency: homeCurrency,
    displayCurrency: reporting.currency === homeCurrency ? null : reporting.currency,
    rates: fxRates ?? {},
    // Dates follow the COUNTRY, not the pinned currency: reading the book in
    // USD should not silently turn every Indian date into mm/dd.
    dateLocale: countryDef(viewCountry)?.locale || me?.tenant_locale || "en-IN",
  });
  // Same reasoning: resolve before children render, so nav labels and dir are
  // right on first paint rather than flipping a frame later.
  resolveLocale(me?.tenant_locale);

  const lensOn = useIsOverridden(me?.tenant_country);
  // Company-name lens: rewrite known debtor names in the rendered panel while
  // the lens points away from home. The main element remounts per lens (see
  // its key), so rewrites never need undoing.
  const mainRef = React.useRef<HTMLElement | null>(null);
  const tabs = visibleTabs(me?.role);
  const [tab, setTab] = useState<string>(() => initialTab(tabs));

  // if the current role can't see the active tab, fall back to its first tab
  const activeId = tabs.some((x) => x.id === tab) ? tab : tabs[0]?.id;

  // <main> is keyed on tab AND lens, so it is a NEW DOM node after either
  // changes — the observer must re-attach to it, or it keeps rewriting a
  // detached node and names revert on every tab switch.
  useEffect(() => {
    if (!lensOn || !mainRef.current) return;
    return attachNameLens(mainRef.current, viewCountry);
  }, [lensOn, viewCountry, activeId]);

  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  // i18n state is module-level (so plain t() calls work anywhere); this bump
  // is what tells React to repaint after a language change.
  const [, bumpLocale] = useState(0);

  const select = (id: string) => {
    setTab(id);
    setMobileNavOpen(false);
    // keep the URL in step so the tab is shareable / reload-safe
    const url = new URL(window.location.href);
    url.searchParams.set("tab", id);
    window.history.replaceState(null, "", url.toString());
  };

  // lock page scroll behind the drawer while it's open; Escape closes it
  useEffect(() => {
    document.body.style.overflow = mobileNavOpen ? "hidden" : "";
    if (!mobileNavOpen) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setMobileNavOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => { document.body.style.overflow = ""; window.removeEventListener("keydown", onKey); };
  }, [mobileNavOpen]);

  const [paletteOpen, setPaletteOpen] = useState(false);
  useCommandPaletteShortcut(paletteOpen, setPaletteOpen);
  const paletteItems = tabs.map((x) => ({ id: x.id, label: t(navKey(x.id, viewCountry)) }));

  const Active = tabs.find((x) => x.id === activeId)?.Panel ?? tabs[0]?.Panel ?? (() => null);

  return (
    <RoleContext.Provider value={{ role: me?.role ?? null, canWrite: canWriteFor(me?.role) }}>
    <SanctuaryOrbs />
    <SanctuaryParticles />
    <div style={{ maxWidth: 1240, margin: "0 auto", font: "14px Inter, system-ui", padding: "0 16px", position: "relative", zIndex: 1 }}>
      <header className="sanctuary-card sanctuary-header"
        style={{
          display: "flex", flexWrap: "wrap", justifyContent: "space-between", alignItems: "center",
          padding: "12px 18px", margin: "16px 0 0", zIndex: 20, rowGap: 10,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
          <button className="sanctuary-menu-btn sanctuary-btn" onClick={() => setMobileNavOpen((v) => !v)}
            aria-label={t("app.toggleNav")} aria-expanded={mobileNavOpen}>
            ☰
          </button>
          <strong className="font-devotional-display" style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 20, letterSpacing: ".02em", minWidth: 0 }}>
            {/* 42px, not smaller: below ~40 the emblem's figures and wordmark
                turn to mush, which is both weak design and no way to render
                this particular imagery. */}
            <CsmLogo size={42} className="sanctuary-reveal" alt="Chandrika Seva Mission" />
            <span style={{ display: "flex", flexDirection: "column", lineHeight: 1.15, minWidth: 0 }}>
              <span className="sanctuary-glow-text" style={{
                background: "linear-gradient(120deg, var(--accent-gold), var(--accent-pink), var(--accent-purple))",
                WebkitBackgroundClip: "text", backgroundClip: "text", color: "transparent",
              }}>
                Radha Rasvyn
              </span>
              <span className="font-devotional-copy" style={{ fontSize: 10, letterSpacing: ".13em", textTransform: "uppercase", color: "var(--fg-muted)", fontWeight: 500 }}>
                {t("app.tagline")}
              </span>
            </span>
          </strong>
        </div>
        <div className="sanctuary-header-actions" style={{ display: "flex", alignItems: "center", gap: 12 }}>
          {me && (
            <span style={{ fontSize: 13, textAlign: "right", lineHeight: 1.25 }}>
              <span style={{ fontWeight: 600 }}>{me.full_name}</span>
              <br />
              <span className="font-devotional-copy" style={{ color: "var(--fg-muted)", fontStyle: "italic" }}>{ROLE_LABEL[me.role] ?? me.role}</span>
            </span>
          )}
          <PaletteHint onOpen={() => setPaletteOpen(true)} />
          <CountrySwitcher tenantCountry={me?.tenant_country} />
          <CurrencySwitcher tenantCountry={me?.tenant_country} />
          <LocaleSwitcher tenantTag={me?.tenant_locale ?? null} onChange={() => bumpLocale((n) => n + 1)} />
          <SkinToggle className="sanctuary-btn" />
          <ThemeToggle className="sanctuary-btn" />
          <button
            onClick={() => signOut()}
            className="sanctuary-btn"
            style={{ border: "1px solid transparent", borderRadius: 9, padding: "6px 10px", background: "none", color: "var(--fg-muted)", cursor: "pointer", fontSize: 13, flexShrink: 0 }}
          >
            {t("app.signOut")}
          </button>
        </div>
      </header>

      <div className="sanctuary-shell" style={{ margin: "16px 0 0" }}>
        <div className={`sanctuary-nav-backdrop${mobileNavOpen ? " open" : ""}`} onClick={() => setMobileNavOpen(false)} />
        <nav role="tablist" aria-label="Modules" className={`sanctuary-sidebar${mobileNavOpen ? " sanctuary-sidebar-open" : ""}`}>
          {TAB_GROUPS.map((group) => {
            const groupTabs = tabs.filter((x) => group.ids.includes(x.id));
            if (groupTabs.length === 0) return null;
            return (
              <React.Fragment key={group.label ?? "root"}>
                {group.label && <div className="sanctuary-sidebar-group-label">{t(group.label)}</div>}
                {/* param is `tb`, not `t` — `t` is the translate function and
                    shadowing it here would silently break every label below */}
                {groupTabs.map((tb) => {
                  const active = tb.id === activeId;
                  return (
                    <button
                      key={tb.id}
                      role="tab"
                      aria-selected={active}
                      onClick={() => select(tb.id)}
                      className={`sanctuary-btn sanctuary-tab${active ? " sanctuary-tab-active" : ""}`}
                      style={{
                        padding: "9px 12px", borderRadius: 9, cursor: "pointer",
                        font: `${active ? 600 : 400} 14px Inter`,
                        color: active ? "var(--fg)" : "var(--fg-muted)",
                      }}
                    >
                      {t(navKey(tb.id, viewCountry))}
                    </button>
                  );
                })}
              </React.Fragment>
            );
          })}
        </nav>

        <main ref={mainRef} className="sanctuary-main sanctuary-reveal" key={`${activeId}-${viewCountry}`}>
          <JurisdictionRibbon tenantCountry={me?.tenant_country} />
          <Active />
        </main>
      </div>
      <CommandPalette open={paletteOpen} setOpen={setPaletteOpen} items={paletteItems} onSelect={select} />
    </div>
    </RoleContext.Provider>
  );
}

export default function DirectShell() {
  const { session, loading } = useSession();
  if (loading) return <p style={{ padding: 40 }}>…</p>;
  return session ? <Shell /> : <LoginCard title="Radha" />;
}
